'use client';

import { useCallback, useMemo, useState } from 'react';
import {
  AD_EDGE_TAXONOMY,
  AD_EDGE_TAXONOMY_EDGE_TYPES,
  DEFAULT_SELECTED_AD_EDGE_TYPES,
} from '@terra/shared-contracts-internal-network';

export type AdEdgeSubcategorySelectionState = 'all' | 'none' | 'partial';

export function getSubcategorySelectionState({
  edgeTypes,
  selected,
}: {
  edgeTypes: readonly string[];
  selected: ReadonlySet<string>;
}): AdEdgeSubcategorySelectionState {
  const selectedCount = edgeTypes.filter((edgeType) =>
    selected.has(edgeType),
  ).length;

  if (selectedCount === 0) return 'none';
  if (selectedCount === edgeTypes.length) return 'all';
  return 'partial';
}

export function useAdEdgeFilterSelection() {
  const [selectedEdgeTypes, setSelectedEdgeTypes] = useState<readonly string[]>(
    DEFAULT_SELECTED_AD_EDGE_TYPES,
  );

  const selected = useMemo(
    () => new Set(selectedEdgeTypes),
    [selectedEdgeTypes],
  );

  const toggleEdgeType = useCallback((edgeType: string) => {
    setSelectedEdgeTypes((current) =>
      current.includes(edgeType)
        ? current.filter((candidate) => candidate !== edgeType)
        : [...current, edgeType],
    );
  }, []);

  const setSubcategorySelected = useCallback(
    ({
      edgeTypes,
      isSelected,
    }: {
      edgeTypes: readonly string[];
      isSelected: boolean;
    }) => {
      setSelectedEdgeTypes((current) => {
        const next = new Set(current);
        for (const edgeType of edgeTypes) {
          if (isSelected) {
            next.add(edgeType);
          } else {
            next.delete(edgeType);
          }
        }
        return [...next];
      });
    },
    [],
  );

  const selectAll = useCallback(() => {
    setSelectedEdgeTypes(AD_EDGE_TAXONOMY_EDGE_TYPES);
  }, []);

  const clearAll = useCallback(() => {
    setSelectedEdgeTypes([]);
  }, []);

  const resetToDefault = useCallback(() => {
    setSelectedEdgeTypes(DEFAULT_SELECTED_AD_EDGE_TYPES);
  }, []);

  const isDefaultSelection = useMemo(
    () =>
      selectedEdgeTypes.length === DEFAULT_SELECTED_AD_EDGE_TYPES.length &&
      DEFAULT_SELECTED_AD_EDGE_TYPES.every((edgeType) =>
        selected.has(edgeType),
      ),
    [selected, selectedEdgeTypes],
  );

  return {
    taxonomy: AD_EDGE_TAXONOMY,
    selectedEdgeTypes,
    selected,
    isDefaultSelection,
    toggleEdgeType,
    setSubcategorySelected,
    selectAll,
    clearAll,
    resetToDefault,
  };
}
